import { useMemo } from 'react';
import { LineChart, LineChartProps } from '@mui/x-charts/LineChart';
import { COLORS } from '../../../../../utils/colors';
import { SupplierCreditApplicationMonthSummary } from '@dill/dill-shared';

interface TimeToDecisionChartProps {
  selectedYear: string;
  monthlyData: SupplierCreditApplicationMonthSummary[];
}

const getResponsiveConfig = (): Partial<LineChartProps> => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  return {
    height: isMobile ? 350 : 400,
    margin: {
      left: isMobile ? 60 : 80,
      right: isMobile ? 60 : 80,
      top: 20,
      bottom: isMobile ? 80 : 60,
    },
  };
};

function TimeToDecisionChart({ selectedYear, monthlyData }: TimeToDecisionChartProps) {
  const chartData = useMemo(() => {
    return monthlyData.length > 0
      ? [...monthlyData]
          .sort((a, b) => parseInt(a.month) - parseInt(b.month))
          .map((data) => ({
            month: data.monthName.substring(0, 3), // Convert "January" to "Jan"
            averageTimeToDecision: data.averageTimeToDecision,
          }))
      : [];
  }, [monthlyData]);

  return (
    <div className="w-full">
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4 mb-6">
        <div>
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-1">
            Time to Decision Analysis
          </h3>
          <p className="text-sm text-gray-500">
            Average monthly time to decision for {selectedYear}
          </p>
        </div>
      </div>

      <LineChart
        dataset={chartData}
        xAxis={[
          {
            dataKey: 'month',
            scaleType: 'point',
            tickLabelStyle: { fontSize: 12, fill: COLORS.TEXT_SECONDARY },
          },
        ]}
        series={[
          {
            dataKey: 'averageTimeToDecision',
            label: 'Average Time to Decision',
            color: COLORS.INFO_500,
            curve: 'linear',
            valueFormatter: (value: number | null) => {
              return `${value || 0} hours`;
            },
          },
        ]}
        yAxis={[
          {
            width: 90,
            tickLabelStyle: { fontSize: 11, fill: COLORS.TEXT_SECONDARY },
            min: 0,
            valueFormatter: (value: number) => {
              return `${value}h`;
            },
          },
        ]}
        grid={{ horizontal: true, vertical: false }}
        {...getResponsiveConfig()}
      />
    </div>
  );
}

export default TimeToDecisionChart;
